import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const TimerContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within TimerProvider');
  }
  return context;
};

export const TimerProvider = ({ children }) => {
  const { getAuthHeader, isAuthenticated } = useAuth();
  const [activeEntry, setActiveEntry] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const fetchActiveEntry = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/time-entries/active`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setActiveEntry(response.data);
        setIsRunning(true);
        setIsPaused(response.data.is_paused || false);
        const start = new Date(response.data.start_time);
        const now = response.data.is_paused && response.data.paused_at ? new Date(response.data.paused_at) : new Date();
        const totalElapsed = Math.floor((now - start) / 1000);
        const pauseDuration = response.data.total_pause_duration || 0;
        setElapsedTime(Math.max(0, totalElapsed - pauseDuration));
      } else {
        setActiveEntry(null);
        setIsRunning(false);
        setIsPaused(false);
        setElapsedTime(0);
      }
    } catch (error) {
      console.error('Failed to fetch active timer entry:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchActiveEntry();
    }
  }, [isAuthenticated, fetchActiveEntry]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const sync = () => { if (!document.hidden) fetchActiveEntry(); };
    const interval = setInterval(sync, 15000);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [isAuthenticated, fetchActiveEntry]);

  useEffect(() => {
    let interval;
    if (isRunning && !isPaused && activeEntry) {
      interval = setInterval(() => {
        const start = new Date(activeEntry.start_time);
        const now = new Date();
        const totalElapsed = Math.floor((now - start) / 1000);
        const pauseDuration = activeEntry.total_pause_duration || 0;
        setElapsedTime(totalElapsed - pauseDuration);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, isPaused, activeEntry]);

  const startTimer = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/time-entries`,
        { start_time: new Date().toISOString() },
        { headers: getAuthHeader() }
      );
      setActiveEntry(response.data);
      setIsRunning(true);
      setIsPaused(false);
      setElapsedTime(0);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const pauseTimer = async () => {
    if (!activeEntry) return;
    try {
      await axios.put(
        `${API_URL}/time-entries/${activeEntry.id}/pause`,
        {},
        { headers: getAuthHeader() }
      );
      setIsPaused(true);
    } catch (error) {
      throw error;
    }
  };

  const resumeTimer = async () => {
    if (!activeEntry) return;
    try {
      await axios.put(
        `${API_URL}/time-entries/${activeEntry.id}/resume`,
        {},
        { headers: getAuthHeader() }
      );
      setIsPaused(false);
      await fetchActiveEntry();
    } catch (error) {
      throw error;
    }
  };

  const stopTimer = async (description = null) => {
    if (!activeEntry) return;
    try {
      const payload = { end_time: new Date().toISOString() };
      if (description) {
        payload.description = description;
      }
      const response = await axios.put(
        `${API_URL}/time-entries/${activeEntry.id}`,
        payload,
        { headers: getAuthHeader() }
      );
      setActiveEntry(null);
      setIsRunning(false);
      setIsPaused(false);
      setElapsedTime(0);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <TimerContext.Provider
      value={{
        activeEntry,
        elapsedTime,
        isRunning,
        isPaused,
        startTimer,
        pauseTimer,
        resumeTimer,
        stopTimer,
        formatTime,
        refreshActiveEntry: fetchActiveEntry
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};
